import {
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    orderBy,
    addDoc,
    updateDoc,
    deleteDoc,
    doc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { errorHandler } from '../utils/errorHandler';
import { Announcement } from '../types/announcement';

const COLLECTION_NAME = 'announcements';

export interface CreateAnnouncementData {
    title: string;
    content: string;
    type: 'info' | 'warning' | 'error' | 'success';
    startDate: Date;
    endDate?: Date | null;
    isActive: boolean;
}

export interface UpdateAnnouncementData extends Partial<CreateAnnouncementData> {
    updatedAt?: Date;
}

export const announcementService = {
    // Aktif duyuruları getir
    getActiveAnnouncements: async (): Promise<Announcement[]> => {
        try {
            errorHandler.log('Fetching active announcements, current time:', new Date().toISOString());
            
            const q = query(
                collection(db, COLLECTION_NAME),
                where('isActive', '==', true),
                orderBy('startDate', 'desc')
            );
            
            const querySnapshot = await getDocs(q);
            const announcements = querySnapshot.docs.map(doc => {
                const data = doc.data();
                errorHandler.log('Raw announcement data:', data);
                return {
                    id: doc.id,
                    ...data,
                    startDate: data.startDate instanceof Timestamp ? data.startDate.toDate().toISOString() : data.startDate,
                    endDate: data.endDate instanceof Timestamp ? data.endDate.toDate().toISOString() : data.endDate,
                    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
                    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt
                } as Announcement;
            });

            // Şu anki tarihle karşılaştırarak aktif duyuruları filtrele
            const now = new Date();
            const filteredAnnouncements = announcements.filter(announcement => {
                errorHandler.log('Checking announcement:', announcement);
                const startDate = new Date(announcement.startDate);
                const endDate = announcement.endDate ? new Date(announcement.endDate) : null;

                if (startDate > now) {
                    errorHandler.log('Announcement filtered out - future start date');
                    return false;
                }

                if (endDate && endDate < now) {
                    errorHandler.log('Announcement filtered out - past end date');
                    return false;
                }

                return true;
            });

            errorHandler.log('Final filtered announcements:', filteredAnnouncements);
            return filteredAnnouncements;
        } catch (error) {
            errorHandler.handleError(error, 'Error fetching active announcements');
            return [];
        }
    },

    // Tüm duyuruları getir (admin için)
    getAllAnnouncements: async (): Promise<Announcement[]> => {
        try {
            const q = query(
                collection(db, COLLECTION_NAME),
                orderBy('createdAt', 'desc')
            );
            
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    startDate: data.startDate instanceof Timestamp ? data.startDate.toDate().toISOString() : data.startDate,
                    endDate: data.endDate instanceof Timestamp ? data.endDate.toDate().toISOString() : data.endDate,
                    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
                    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : data.updatedAt
                } as Announcement;
            });
        } catch (error) {
            errorHandler.handleError(error, 'Error fetching all announcements');
            return [];
        }
    },

    // Yeni duyuru oluştur
    createAnnouncement: async (data: CreateAnnouncementData): Promise<string> => {
        try {
            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...data,
                startDate: Timestamp.fromDate(data.startDate),
                endDate: data.endDate ? Timestamp.fromDate(data.endDate) : null,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
            return docRef.id;
        } catch (error) {
            errorHandler.handleError(error, 'Error creating announcement');
            throw error;
        }
    },

    // Duyuru güncelle
    updateAnnouncement: async (id: string, data: UpdateAnnouncementData): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const updateData: any = {
                ...data,
                updatedAt: Timestamp.now()
            };

            if (data.startDate) {
                updateData.startDate = Timestamp.fromDate(data.startDate);
            }
            if (data.endDate) {
                updateData.endDate = Timestamp.fromDate(data.endDate);
            }

            await updateDoc(docRef, updateData);
        } catch (error) {
            errorHandler.handleError(error, 'Error updating announcement');
            throw error;
        }
    },

    // Duyuru sil
    deleteAnnouncement: async (id: string): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await deleteDoc(docRef);
        } catch (error) {
            errorHandler.handleError(error, 'Error deleting announcement');
            throw error;
        }
    }
}; 